import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { handleMobileInput, handleGSTInput, validateMobile, validateGST } from '@/utils/inputValidation';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Users, Eye, Edit, Trash2, UserCheck, UserX, Search, UserPlus, MoveRight, CheckSquare, Square, ExternalLink, Wallet, CreditCard, ShieldCheck, RefreshCw } from 'lucide-react';
import { ListFilter } from 'lucide-react';
import { PasswordStrengthChecklist, validatePasswordPolicy } from '@/components/PasswordStrengthChecklist';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function AdminClients() {
  const { canDelete, hasPermission } = useAuth();
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAdminCreatedOnly, setShowAdminCreatedOnly] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveToParentId, setMoveToParentId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Permission checks
  const canViewClients = hasPermission('clients:view');
  const canCreateClient = hasPermission('clients:create');
  const canEditClient = hasPermission('clients:edit');
  const canSuspendClient = hasPermission('partners:suspend');
  const canDeleteClient = hasPermission('clients:delete') && canDelete;
  const canCreditClientWallet = hasPermission('clients:credit_wallet');
  const canAccessClientDashboard = hasPermission('clients:dashboard_access');
  
  const [editForm, setEditForm] = useState({
    company_name: '',
    email: '',
    phone: '',
    gst_number: '',
    address: ''
  });
  const [newClientForm, setNewClientForm] = useState({
    email: '',
    password: '',
    company_name: '',
    phone: '',
    gst_number: '',
    address: '',
    parent_id: ''
  });
  
  // Credit wallet state
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditClient, setCreditClient] = useState(null);

  // Support PIN Dashboard Access state (Case B clients only)
  const [clientPinDialog, setClientPinDialog] = useState({ open: false, client: null });
  const [clientPinInput, setClientPinInput] = useState('');
  const [clientPinLoading, setClientPinLoading] = useState(false);

  const handleClientDashboardViaPin = (client) => {
    setClientPinInput('');
    setClientPinDialog({ open: true, client });
  };

  const submitClientPinAccess = async () => {
    const { client } = clientPinDialog;
    if (!clientPinInput.trim()) { toast.error('Please enter the PIN'); return; }
    setClientPinLoading(true);
    try {
      const res = await axios.post(`${API}/support-pin/validate`, {
        user_email: client.email,
        pin: clientPinInput.trim(),
        access_type: 'client',
        reason: 'Admin support access via client-shared PIN',
      });
      const { access_token } = res.data;
      setClientPinDialog({ open: false, client: null });
      toast.success('PIN verified — opening client dashboard...');
      const clientDisplayName = encodeURIComponent(client.company_name || client.email);
      window.open(`/auto-login?token=${access_token}&redirect=/client/billing&masquerade=1&cn=${clientDisplayName}`, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired PIN');
    } finally {
      setClientPinLoading(false);
    }
  };
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');

  useEffect(() => {
    fetchClients();
    fetchPartners();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/resellers`);
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners');
    }
  };

  const viewClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`${API}/admin/clients/${clientId}`);
      setSelectedClient(response.data);
      setDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch client details');
    }
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setEditForm({
      company_name: client.company_name || '',
      email: client.email || '',
      phone: client.phone || '',
      gst_number: client.gst_number || '',
      address: client.address || ''
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/admin/clients/${selectedClient.id}`, editForm);
      toast.success('Client updated successfully');
      setEditOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleSuspend = async (clientId) => {
    if (!window.confirm('Are you sure you want to suspend this client?')) return;
    try {
      await axios.post(`${API}/admin/clients/${clientId}/suspend`);
      toast.success('Client suspended');
      fetchClients();
    } catch (error) {
      toast.error('Failed to suspend client');
    }
  };

  const handleActivate = async (clientId) => {
    try {
      await axios.post(`${API}/admin/clients/${clientId}/activate`);
      toast.success('Client activated');
      fetchClients();
    } catch (error) {
      toast.error('Failed to activate client');
    }
  };

  const handleCancel = async (clientId) => {
    if (!window.confirm('Are you sure you want to cancel this client? This action cannot be undone.')) return;
    try {
      await axios.post(`${API}/admin/clients/${clientId}/cancel`);
      toast.success('Client cancelled');
      fetchClients();
    } catch (error) {
      toast.error('Failed to cancel client');
    }
  };

  // Credit wallet handler
  const handleCreditWallet = async () => {
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await axios.post(`${API}/admin/wallet/credit`, null, {
        params: {
          user_id: creditClient.id,
          amount: parseFloat(creditAmount),
          description: creditDescription || 'Wallet credit by admin'
        }
      });
      toast.success('Wallet credited successfully');
      setCreditDialogOpen(false);
      setCreditAmount('');
      setCreditDescription('');
      setCreditClient(null);
      fetchClients(); // Refresh to show updated balance
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit wallet');
    }
  };

  const openCreditDialog = (client) => {
    setCreditClient(client);
    setCreditAmount('');
    setCreditDescription('');
    setCreditDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete clients');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  // Login As (Access Dashboard in new tab)
  const handleLoginAs = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/login-as/${userId}`);
      const { access_token, user } = response.data;
      
      // Open new tab with auto-login
      const clientName = encodeURIComponent(user.company_name || user.email || 'Client');
      const loginUrl = `${BASE_URL}/auto-login?token=${access_token}&redirect=/client/dashboard&masquerade=1&cn=${clientName}`;
      window.open(loginUrl, '_blank');
      
      toast.success(`Opening ${user.company_name || user.email}'s dashboard in new tab`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to access dashboard');
    }
  };

  // Create client
  const handleCreateClient = async () => {
    if (!newClientForm.email || !newClientForm.password || !newClientForm.company_name) {
      toast.error('Please fill required fields');
      return;
    }
    if (!validatePasswordPolicy(newClientForm.password)) {
      toast.error('Password must be at least 8 characters with uppercase, lowercase, number, and special character');
      return;
    }
    const phoneErr = validateMobile(newClientForm.phone);
    if (phoneErr) { toast.error(phoneErr); return; }
    if (newClientForm.gst_number) {
      const gstErr = validateGST(newClientForm.gst_number);
      if (gstErr) { toast.error(gstErr); return; }
    }

    try {
      await axios.post(`${API}/admin/clients/create?parent_id=${newClientForm.parent_id || ''}`, {
        email: newClientForm.email,
        password: newClientForm.password,
        company_name: newClientForm.company_name,
        phone: newClientForm.phone,
        gst_number: newClientForm.gst_number,
        address: newClientForm.address
      });
      toast.success('Client created successfully');
      setAddClientOpen(false);
      setNewClientForm({
        email: '',
        password: '',
        company_name: '',
        phone: '',
        gst_number: '',
        address: '',
        parent_id: ''
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  // Move client
  const handleMove = (client) => {
    setSelectedClient(client);
    setMoveToParentId(client.parent_id || '');
    setMoveDialogOpen(true);
  };

  const handleMoveSubmit = async () => {
    try {
      await axios.post(`${API}/admin/users/move`, {
        user_id: selectedClient.id,
        new_parent_id: moveToParentId || null
      });
      toast.success('Client moved successfully');
      setMoveDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move client');
    }
  };

  // Bulk selection
  const handleSelectAll = () => {
    if (selectedIds.length === filteredClients.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredClients.map(c => c.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) {
      toast.error('No clients selected');
      return;
    }

    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
    if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.length} client(s)?`)) return;

    try {
      const response = await axios.post(`${API}/admin/users/bulk-action`, {
        user_ids: selectedIds,
        action: action
      });
      
      if (response.data.success > 0) {
        toast.success(`${actionLabel} completed: ${response.data.success} successful`);
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} failed`);
      }
      
      setSelectedIds([]);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} clients`);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.account_id?.toLowerCase().includes(searchTerm.toLowerCase())||
      client.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && client.is_active !== false && client.status !== 'cancelled') ||
      (statusFilter === 'suspended' && client.is_active === false && client.status !== 'cancelled') ||
      (statusFilter === 'cancelled' && client.status === 'cancelled');

    const matchesAdminFilter = !showAdminCreatedOnly || client.created_by_admin_direct === true;
    
    return matchesSearch && matchesStatus && matchesAdminFilter;
  });

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.is_active !== false && c.status !== 'cancelled').length,
    suspended: clients.filter(c => c.is_active === false && c.status !== 'cancelled').length,
    cancelled: clients.filter(c => c.status === 'cancelled').length
  };

  // Get parent name helper
  const getParentName = (parentId) => {
    if (!parentId) return 'Admin';
    const parent = partners.find(p => p.id === parentId);
    return parent ? parent.company_name : 'Unknown';
  };

  return (
    <DashboardLayout role="admin">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-start flex-wrap md:nowrap">
          <div className="mb-3">
            <h1 className="text-3xl font-bold text-slate-800" data-testid="clients-title">Clients</h1>
            <p className="text-slate-500 mt-1">View and manage all clients</p>
          </div>
          {canCreateClient && (
            <div className="flex items-center gap-2">
              <button onClick={fetchClients} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 text-slate-500" /></button>
              <Button 
                onClick={() => setAddClientOpen(true)}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="add-client-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-8">
            <button
              onClick={() => setStatusFilter('all')}
              className={`pb-3 text-sm font-medium transition-colors relative
                ${statusFilter === 'all' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
            >
              All Clients
              {statusFilter === 'all' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`pb-3 text-sm font-medium transition-colors relative
                ${statusFilter === 'active' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
            >
              Active
              {statusFilter === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
            <button
              onClick={() => setStatusFilter('suspended')}
              className={`pb-3 text-sm font-medium transition-colors relative
                ${statusFilter === 'suspended' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}
              `}
            >
              Suspended
              {statusFilter === 'suspended' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
            </button>
          </div>
        </div>

        {/* Filters and Bulk Actions */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4 mb-2">
            <div className="flex items-center justify-between gap-4 flex-wrap md:nowrap">
              <div className="flex gap-4 justify-between md:justify-start w-full md:w-auto">
              <div className="flex items-center gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-12 bg-white" data-testid="status-filter">
                    <ListFilter className="h-4 w-4 text-slate-600" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Bulk Actions */}
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2 ml-4 pl-4 border-l border-slate-200">
                    <span className="text-sm text-slate-600">{selectedIds.length} selected</span>
                    <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleBulkAction('activate')}>
                      Activate
                    </Button>
                    <Button size="sm" variant="outline" className="text-orange-600" onClick={() => handleBulkAction('suspend')}>
                      Suspend
                    </Button>
                    <Button size="sm" variant="outline" className="text-gray-600" onClick={() => handleBulkAction('cancel')}>
                      Cancel
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleBulkAction('delete')}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowAdminCreatedOnly(v => !v)}
                className={`text-nowrap flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${showAdminCreatedOnly ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                data-testid="filter-admin-created"
                title="Show only clients created directly by Admin"
              >
                <CheckSquare className={`h-4 w-4 ${showAdminCreatedOnly ? 'text-blue-600' : 'text-slate-400'}`} />
                Admin-created only
              </button>
              </div>
              <div className="relative w-full md:w-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-full md:w-80 bg-white"
                  data-testid="search-clients"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clients Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No clients found</h3>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <button onClick={handleSelectAll} className="text-slate-400 hover:text-slate-600">
                          {selectedIds.length === filteredClients.length && filteredClients.length > 0 ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Account ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Company</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Phone</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Parent</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredClients.map(client => (
                      <tr key={client.id} data-testid={`client-row-${client.id}`} className={selectedIds.includes(client.id) ? 'bg-blue-50' : ''}>
                        <td className="px-4 py-3">
                          <button onClick={() => handleSelectOne(client.id)} className="text-slate-400 hover:text-slate-600">
                            {selectedIds.includes(client.id) ? (
                              <CheckSquare className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-slate-500 text-nowrap">{client.account_id || '—'}</td>
                        <td className="px-4 py-3 font-medium text-slate-800 text-nowrap">{client.company_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-blue-600 text-nowrap">{client.email}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 text-nowrap">{client.phone || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 text-nowrap">
                          {client.parent?.company_name || getParentName(client.parent_id)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                            client.status === 'cancelled'
                              ? 'bg-gray-100 text-gray-700'
                              : client.is_active !== false
                                ? 'bg-green-50 text-green-700'
                                : 'bg-red-50 text-red-700'
                          }`}>
                            {client.status === 'cancelled' ? 'Cancelled' : client.is_active !== false ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewClientDetails(client.id)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                              data-testid={`view-${client.id}`}
                              title="View Profile"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {canAccessClientDashboard && (
                              <button
                                onClick={() => handleLoginAs(client.id)}
                                className="text-sm text-cyan-600 hover:text-cyan-800"
                                data-testid={`login-as-${client.id}`}
                                title="Access Dashboard Directly (New Tab)"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </button>
                            )}
                            {/* PIN-based access — Case B (admin-direct) clients only */}
                            {client.created_by_admin_direct === true && (
                              <button
                                onClick={() => handleClientDashboardViaPin(client)}
                                className="text-sm text-emerald-600 hover:text-emerald-800"
                                data-testid={`pin-access-${client.id}`}
                                title="Client Dashboard Access via Support PIN"
                              >
                                <ShieldCheck className="h-4 w-4" />
                              </button>
                            )}
                            {canEditClient && (
                              <button
                                onClick={() => openEditDialog(client)}
                                className="text-sm text-blue-600 hover:text-blue-800"
                                data-testid={`edit-${client.id}`}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {canEditClient && (
                              <button
                                onClick={() => handleMove(client)}
                                className="text-sm text-purple-600 hover:text-purple-800"
                                data-testid={`move-${client.id}`}
                                title="Move to another parent"
                              >
                                <MoveRight className="h-4 w-4" />
                              </button>
                            )}
                            {canCreditClientWallet && client.created_by_admin_direct === true && (
                              <button
                                onClick={() => openCreditDialog(client)}
                                className="text-sm text-emerald-600 hover:text-emerald-800"
                                data-testid={`credit-wallet-${client.id}`}
                                title="Credit Wallet"
                              >
                                <Wallet className="h-4 w-4" />
                              </button>
                            )}
                            {client.status !== 'cancelled' && canSuspendClient && (
                              <>
                                {client.is_active !== false ? (
                                  <button
                                    onClick={() => handleSuspend(client.id)}
                                    className="text-sm text-orange-600 hover:text-orange-800"
                                    data-testid={`suspend-${client.id}`}
                                    title="Suspend"
                                  >
                                    <UserX className="h-4 w-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleActivate(client.id)}
                                    className="text-sm text-green-600 hover:text-green-800"
                                    data-testid={`activate-${client.id}`}
                                    title="Activate"
                                  >
                                    <UserCheck className="h-4 w-4" />
                                  </button>
                                )}
                              </>
                            )}
                            {canDeleteClient && (
                              <button
                                onClick={() => handleDelete(client.id)}
                                className="text-sm text-red-600 hover:text-red-800"
                                data-testid={`delete-${client.id}`}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-details-dialog">
            <DialogHeader>
              <DialogTitle>Client Details</DialogTitle>
            </DialogHeader>
            {selectedClient && (
              <div className="space-y-4">
                {/* Company Logo */}
                {selectedClient.logo_url && (
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      <img 
                        src={`${BASE_URL}${selectedClient.logo_url}`}
                        alt="Company Logo"
                        data-testid="client-logo"
                        className="h-24 w-auto max-w-[200px] object-contain rounded-lg border border-slate-200 p-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Company Name</p>
                    <p className="font-medium">{selectedClient.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{selectedClient.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium">{selectedClient.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">GST Number</p>
                    <p className="font-medium">{selectedClient.gst_number || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Address</p>
                    <p className="font-medium">{selectedClient.address || '-'}</p>
                  </div>
                  {/* Wallet Balance — only for admin-direct (Case B) clients */}
                  {selectedClient.wallet_balance !== undefined && selectedClient.created_by_admin_direct === true && (
                    <>
                      <div>
                        <p className="text-sm text-slate-500">Wallet Balance</p>
                        <p className="font-medium text-green-600" data-testid="client-wallet-balance">₹{(selectedClient.wallet_balance || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Locked Funds</p>
                        <p className="font-medium text-orange-600" data-testid="client-locked-funds">₹{(selectedClient.locked_funds || 0).toLocaleString()}</p>
                      </div>
                    </>
                  )}
                </div>
                
                {selectedClient.parent && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Parent Reseller</p>
                    <div className="bg-slate-50 p-3 rounded-lg text-sm">
                      <p>{selectedClient.parent.company_name || selectedClient.parent.email}</p>
                      <p className="text-slate-500">{selectedClient.parent.email}</p>
                    </div>
                  </div>
                )}

                {selectedClient.orders && selectedClient.orders.length > 0 && (
                  <div className="border-t pt-4 mt-3">
                    <p className="text-sm font-medium mb-2">Orders ({selectedClient.orders.length})</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedClient.orders.map(order => (
                        <div key={order.id} className="bg-[#115ab4]/10 p-3 rounded-lg text-sm flex justify-between mb-1">
                          <span>{order.product_name}</span>
                          <span className="font-medium">₹{order.amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Client Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={editForm.company_name}
                  onChange={(e) => setEditForm(prev => ({...prev, company_name: e.target.value}))}
                  data-testid="edit-company"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({...prev, email: e.target.value}))}
                  data-testid="edit-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm(prev => ({...prev, phone: handleMobileInput(e.target.value)}))}
                  data-testid="edit-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input
                  value={editForm.gst_number}
                  onChange={(e) => setEditForm(prev => ({...prev, gst_number: handleGSTInput(e.target.value)}))}
                  data-testid="edit-gst"
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={editForm.address}
                  onChange={(e) => setEditForm(prev => ({...prev, address: e.target.value}))}
                  data-testid="edit-address"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate} data-testid="save-client-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Client Dialog */}
        <Dialog open={addClientOpen} onOpenChange={setAddClientOpen}>
          <DialogContent className="max-w-lg" data-testid="add-client-dialog">
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newClientForm.email}
                    onChange={(e) => setNewClientForm(prev => ({...prev, email: e.target.value}))}
                    placeholder="client@example.com"
                    data-testid="new-client-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newClientForm.password}
                    onChange={(e) => setNewClientForm(prev => ({...prev, password: e.target.value}))}
                    placeholder="Min 8 characters"
                    data-testid="new-client-password"
                  />
                  <PasswordStrengthChecklist password={newClientForm.password} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={newClientForm.company_name}
                  onChange={(e) => setNewClientForm(prev => ({...prev, company_name: e.target.value}))}
                  placeholder="Company Name"
                  data-testid="new-client-company"
                />
              </div>
              <div className="space-y-2">
                <Label>Assign to Partner</Label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2"
                  value={newClientForm.parent_id}
                  onChange={(e) => setNewClientForm(prev => ({...prev, parent_id: e.target.value}))}
                  data-testid="new-client-parent"
                >
                  <option value="">Direct to Admin</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>
                      {partner.company_name} ({partner.role === 'reseller' ? 'Synergy' : 'Channel'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={newClientForm.phone}
                    onChange={(e) => setNewClientForm(prev => ({...prev, phone: handleMobileInput(e.target.value)}))}
                    placeholder="Phone number"
                    data-testid="new-client-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GST Number</Label>
                  <Input
                    value={newClientForm.gst_number}
                    onChange={(e) => setNewClientForm(prev => ({...prev, gst_number: handleGSTInput(e.target.value)}))}
                    placeholder="GST Number"
                    data-testid="new-client-gst"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={newClientForm.address}
                  onChange={(e) => setNewClientForm(prev => ({...prev, address: e.target.value}))}
                  placeholder="Address"
                  data-testid="new-client-address"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddClientOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateClient} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="create-client-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Create Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Move Client Dialog */}
        <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
          <DialogContent className="max-w-md" data-testid="move-client-dialog">
            <DialogHeader>
              <DialogTitle>Move Client</DialogTitle>
            </DialogHeader>
            {selectedClient && (
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-600">
                  Moving: <span className="font-semibold">{selectedClient.company_name || selectedClient.email}</span>
                </p>
                <div className="space-y-2">
                  <Label>Select New Parent</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    value={moveToParentId}
                    onChange={(e) => setMoveToParentId(e.target.value)}
                    data-testid="move-client-parent"
                  >
                    <option value="">Direct to Admin</option>
                    {partners.map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.company_name} ({partner.role === 'reseller' ? 'Synergy' : 'Channel'})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">
                    Select "Direct to Admin" to move under admin management
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMoveSubmit} className="bg-purple-600 hover:bg-purple-700 text-white">
                <MoveRight className="h-4 w-4 mr-2" />
                Move Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credit Wallet Dialog */}
        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent data-testid="client-credit-wallet-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-emerald-600" />
                Credit Client Wallet
              </DialogTitle>
            </DialogHeader>
            {creditClient && (
              <div className="space-y-4">
                <div className="bg-[#10996e]/10 p-4 rounded-md">
                  <p className="text-sm text-slate-600">Client</p>
                  <p className="font-medium">{creditClient.company_name || creditClient.email}</p>
                  <p className="text-sm text-slate-600 mt-2">Email</p>
                  <p className="font-medium">{creditClient.email}</p>
                  {creditClient.wallet_balance !== undefined && (
                    <>
                      <p className="text-sm text-slate-600 mt-2">Current Balance</p>
                      <p className="font-medium text-green-600">₹{(creditClient.wallet_balance || 0).toLocaleString()}</p>
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Amount (INR) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="5000"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    data-testid="client-credit-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Description (Optional)</Label>
                  <Input
                    placeholder="Wallet credit by admin"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    data-testid="client-credit-description"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreditWallet} 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="client-credit-submit"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Credit Wallet
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* ─── Support PIN Dashboard Access Dialog (Case B clients) ─── */}
      <Dialog open={clientPinDialog.open} onOpenChange={(o) => !o && setClientPinDialog({ open: false, client: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Client Dashboard Access via PIN
            </DialogTitle>
          </DialogHeader>
          {clientPinDialog.client && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                <p className="text-emerald-800 font-medium">{clientPinDialog.client.company_name || clientPinDialog.client.email}</p>
                <p className="text-emerald-600 text-xs mt-0.5">{clientPinDialog.client.email}</p>
              </div>
              <p className="text-sm text-slate-600">
                Ask the client to share their 6-digit Support PIN from their dashboard. Enter it below to gain temporary access.
              </p>
              <div className="space-y-2">
                <Label className="text-sm font-medium">6-digit Support PIN</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={clientPinInput}
                  onChange={(e) => setClientPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-xl font-mono tracking-widest h-12"
                  data-testid="client-pin-input"
                  onKeyDown={(e) => e.key === 'Enter' && submitClientPinAccess()}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientPinDialog({ open: false, client: null })}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={submitClientPinAccess}
              disabled={clientPinLoading || clientPinInput.length !== 6}
              data-testid="submit-client-pin-btn"
            >
              {clientPinLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Access Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
